import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { heroImages } from '../../data/mock';
import { langPath } from '../../utils/siteNav';
import { optimized, srcSetFor } from '../../utils/img';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_FOR = { rooms: 'Rooms', amenities: 'Pool', gallery: 'Exterior', 'virtual-tour': 'Terrace', location: 'Exterior', reviews: 'Terrace', faq: 'Rooms', contact: 'Exterior', guides: 'Terrace' };
let galleryCache = null;

// Compact hero for sub-pages: photo, title, subtitle and a Home › … › Page breadcrumb (also gives the fixed transparent header a dark backdrop).
// `image` skips the gallery lookup; `crumbs` ([{ label, to }]) adds intermediate levels (e.g. Home › Rooms › Double Room).
export default function PageBanner({ slug, title, subtitle, image, crumbs = [], compact = false }) {
  const { language, t } = useLanguage();
  const [img, setImg] = useState(image || heroImages[0]);
  useEffect(() => {
    if (image) { setImg(image); return undefined; }
    let alive = true;
    const pick = (items) => { const cat = CATEGORY_FOR[slug]; const m = items.find((g) => g.category === cat) || items[0]; const u = m?.image || m?.url; if (alive && u) setImg(u); };
    if (galleryCache) pick(galleryCache);
    else fetch(`${API_URL}/api/public/gallery`).then((r) => r.json()).then((j) => { galleryCache = Array.isArray(j) ? j : (j.gallery || j.items || []); pick(galleryCache); }).catch(() => {});
    return () => { alive = false; };
  }, [slug, image]);
  return (
    <section className={`relative ${compact ? 'min-h-[300px] md:min-h-[320px] pt-36 pb-8' : 'h-[46vh] min-h-[320px]'} flex items-end overflow-hidden bg-slate-900`} data-testid={`page-banner-${slug}`}>
      <img src={optimized(img, 1600)} srcSet={srcSetFor(img, [800, 1200, 1600])} sizes="100vw" alt={title} className="absolute inset-0 w-full h-full object-cover opacity-70" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-900/30" />
      <div className="relative max-w-7xl mx-auto px-6 pb-12 w-full">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-white/70 mb-4" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to={langPath(language)} className="hover:text-amber-300">{t('pages.home')}</Link>
          {crumbs.map((c) => (
            <React.Fragment key={c.to}>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to={c.to} className="hover:text-amber-300">{c.label}</Link>
            </React.Fragment>
          ))}
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-amber-300">{title}</span>
        </nav>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white leading-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-base md:text-lg text-white/80 mt-3 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}
